import useFromPrice, { FromPriceConfig } from "./useFromPrice";

const formatUsd = (amount: number) =>
  amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Resolves the monthly (and, when `isYearly`, the yearly-equivalent) price for
 * an upsell "plan card" from the pricing API, formatted as currency strings.
 *
 * Shared by the "Most popular plans" card/modal and the upgrade-to-replace
 * modal so the pricing logic lives in one place.
 *
 * - `price`: the monthly price (headline, or strikethrough when yearly is on).
 *   `undefined` while loading.
 * - `discountPrice`: the per-month equivalent of the yearly plan (annual total
 *   ÷ 12), only set when `isYearly` is enabled and resolved.
 */
export default function usePlanCardPrice(
  config: FromPriceConfig,
  isYearly: boolean
) {
  const { price: monthlyPrice } = useFromPrice(config);
  const { price: yearlyAnnualPrice } = useFromPrice(
    isYearly ? { ...config, term: "yearly" } : null
  );

  return {
    price: monthlyPrice === undefined ? undefined : formatUsd(monthlyPrice),
    discountPrice:
      isYearly && yearlyAnnualPrice !== undefined
        ? formatUsd(yearlyAnnualPrice / 12)
        : undefined,
  };
}
